/**
 * Subscription Screen - Hinge-Style Design
 *
 * Features:
 * - Two-tab switcher: Drama+ and DramaX
 * - Horizontal scrollable billing intervals (week/month/quarter/semester/year)
 * - Hero banner with value proposition
 * - Features list with icons
 * - Large CTA button
 * - Separate family plan view
 *
 * Pricing Strategy:
 * - Weekly: Poor value anchor (intentionally expensive)
 * - Monthly: Default option
 * - Quarterly: 10% discount
 * - Semester: 20% discount
 * - Yearly: 25% discount ⭐ Best Value
 */
import { useState, useEffect, useRef } from "react";
import { View, Text, ScrollView, Pressable, Linking, ActivityIndicator, TextInput } from "react-native";
import { useTranslation } from "@dramadetector/shared";
import { useTabScreenPadding } from "../../components/layout/TabScreenWrapper";
import { useAuth } from "../../contexts/AuthContext";
import { useAlert } from "../../contexts/AlertProvider";
import { trackEvent } from "@/lib/analytics/events";
import { type ProductKey, type BillingInterval, TIER_NAMES, PRICING, BILLING_INTERVAL_SUFFIX } from "../../../../lib/polar/constants";
import { GlassBackground, GlassCard } from "../../components/glass";
import { useFamilyPlan } from "../../hooks/useFamilyPlan";
import { useRouter, useLocalSearchParams } from "expo-router";
import { useSubscriptionPricing } from "../../hooks/useSubscriptionPricing";
import { useEntitlements } from "../../hooks/useEntitlements";
import { useSDUIConfig, toUISpecTier } from '@dramadetector/ui/json-render';
import { useSubscription } from "../../hooks/useSubscription";
import { useUpdateSubscription } from "../../hooks/useUpdateSubscription";
import type { TierId, PlanType as CMSPlanType } from "@/lib/cms/client";

// Components
import { TierSwitcher } from "../../components/screens/subscription/TierSwitcher";
import { PlanTypeToggle } from "../../components/screens/subscription/PlanTypeToggle";
import { BillingIntervalSelector } from "../../components/screens/subscription/BillingIntervalSelector";
import { FeaturesSection } from "../../components/screens/subscription/FeaturesSection";
import { PricingCard } from "../../components/screens/subscription/PricingCard";
import { FamilyPlanBanner } from "../../components/screens/subscription/FamilyPlanBanner";
import { PlanChangeCard } from "../../components/screens/subscription/PlanChangeCard";
import { getReferralCode, isValidReferralCode } from "../../lib/referral-storage";
import {
  authenticatedFetch,
  getAuthFailureCategory,
  getAuthFailureReason,
  useAuthReady,
} from "@dramadetector/api-client";
import { getEdgeFunctionUrl, assertResponseOk } from "../../lib/api-url";

type Tier = 'drama_plus' | 'dramax';
type PlanType = 'individual' | 'family';

// Type-safe mapping from local types to CMS types
// These types are structurally identical but defined in different modules
const VALID_TIERS: readonly Tier[] = ['drama_plus', 'dramax'] as const;
const VALID_PLAN_TYPES: readonly PlanType[] = ['individual', 'family'] as const;

/**
 * Type guard to validate tier is a valid CMS TierId.
 * Ensures runtime safety when passing to CMS hooks.
 */
function isValidTier(tier: string): tier is TierId {
  return VALID_TIERS.includes(tier as Tier);
}

/**
 * Type guard to validate plan type is a valid CMS PlanType.
 * Ensures runtime safety when passing to CMS hooks.
 */
function isValidPlanType(planType: string): planType is CMSPlanType {
  return VALID_PLAN_TYPES.includes(planType as PlanType);
}

// Helper function to get billing interval suffix for product key construction
function getBillingIntervalSuffix(interval: BillingInterval): string {
  return `_${BILLING_INTERVAL_SUFFIX[interval]}`;
}

// SDUI: subscription page visibility gates are server-configurable via sdui_surface_configs.
// Previously build-time constants; migrated to SDUI for instant control without app release.
// Defaults match the previous build-time values (false/false/true).
// Note: These replaced PostHog flags that had bucket-stickiness bugs (#1451).
// SDUI fetch-once-and-cache avoids that problem.

export default function SubscriptionScreen() {
  // ✅ Fix: Disable Suspense for lazy-loaded namespace to prevent race condition (subscription namespace not preloaded)
  const { t } = useTranslation('subscription', { useSuspense: false }); // Explicit namespace for subscription screen
  const { t: tCommon } = useTranslation('common'); // Common namespace for shared UI
  const { user, accessToken, loading: authLoading, isRealUser } = useAuth();
  const bottomPadding = useTabScreenPadding();
  const { alert } = useAlert();
  const router = useRouter();
  const { familyPlan, isFamilyMember, isFamilyAdmin } = useFamilyPlan();
  const { source } = useLocalSearchParams<{ source?: string }>();
  const { tier: entitlementTier } = useEntitlements();
  const { updateSubscription } = useUpdateSubscription();

  // SDUI: subscription page feature gates (server-configurable)
  const { config: subPageConfig } = useSDUIConfig('subscription-page', toUISpecTier(entitlementTier));
  const SHOW_DRAMAX_PLANS = subPageConfig.showDramaxPlans;
  const SHOW_FAMILY_PLANS = subPageConfig.showFamilyPlans;
  const CHECKOUT_ENABLED = subPageConfig.checkoutEnabled;

  // Subscriber state detection
  const { subscription, planName: currentPlanName } = useSubscription();
  const isSubscribed = !!subscription && ['active', 'trialing'].includes(subscription.status);
  const currentTier = subscription?.tier;
  const authReady = useAuthReady();
  const hasRealSessionUser = !!user && !user.isAnonymous;
  const isCheckoutAuthPending = hasRealSessionUser && !authReady;

  // Referral incentive banner + checkout attribution (NocoDB #1556)
  // Store the code in a ref so the checkout handler can read it without a second async call.
  const [hasReferral, setHasReferral] = useState(false);
  const referralCodeRef = useRef<string | null>(null);
  useEffect(() => {
    getReferralCode().then((code) => {
      referralCodeRef.current = code;
      setHasReferral(!!code);
    }).catch(() => {});
  }, []);

  // State management
  const [selectedTier, setSelectedTier] = useState<Tier>('drama_plus');
  const [selectedInterval, setSelectedInterval] = useState<BillingInterval>('month');
  const [planType, setPlanType] = useState<PlanType>('individual');
  const [isLoading, setIsLoading] = useState(false);
  const [isPlanChangeLoading, setIsPlanChangeLoading] = useState(false);

  // Sync selected interval to subscriber's current billing interval
  useEffect(() => {
    if (isSubscribed && subscription?.billingInterval) {
      setSelectedInterval(subscription.billingInterval);
    }
  }, [isSubscribed, subscription?.billingInterval]);

  // Billing interval change state for existing subscribers
  const [isChangingInterval, setIsChangingInterval] = useState(false);
  const [billingIntervalError, setBillingIntervalError] = useState<string | null>(null);

  /** Handle interval selection — for subscribers, confirm and call update-subscription */
  const handleIntervalChange = async (newInterval: BillingInterval) => {
    setSelectedInterval(newInterval);

    if (isSubscribed && subscription?.billingInterval && newInterval !== subscription.billingInterval) {
      // Build target product key
      const tierKey = currentTier ?? 'drama_plus';
      const intervalSuffix = BILLING_INTERVAL_SUFFIX[newInterval];
      const targetProductKey = `${tierKey}_${intervalSuffix}` as ProductKey;
      const targetPricing = PRICING[targetProductKey];

      if (!targetPricing) return;

      const currentIntervalLabel = subscription.billingInterval === 'month' ? 'monthly'
        : subscription.billingInterval === 'year' ? 'yearly'
        : subscription.billingInterval === 'week' ? 'weekly'
        : subscription.billingInterval === 'quarter' ? 'quarterly'
        : 'every 6 months';
      const newIntervalLabel = newInterval === 'month' ? 'monthly'
        : newInterval === 'year' ? 'yearly'
        : newInterval === 'week' ? 'weekly'
        : newInterval === 'quarter' ? 'quarterly'
        : 'every 6 months';

      const priceInfo = targetPricing.billedAs
        ? `${targetPricing.displayPrice} (${targetPricing.billedAs})`
        : targetPricing.displayPrice;

      alert(
        t('plan_change.interval_confirm_title', {
          interval: newIntervalLabel,
          defaultValue: `Change to ${newIntervalLabel} billing?`,
        }),
        t('plan_change.interval_confirm_message', {
          from: currentIntervalLabel,
          to: newIntervalLabel,
          price: priceInfo,
          defaultValue: `Switch from ${currentIntervalLabel} to ${newIntervalLabel} at ${priceInfo}. Polar will prorate the difference.`,
        }),
        [
          {
            text: tCommon('cancel'),
            style: 'cancel',
            onPress: () => {
              // Revert to current interval on cancel
              setSelectedInterval(subscription.billingInterval!);
            },
          },
          {
            text: t('plan_change.confirm_change', { defaultValue: 'Change' }),
            onPress: async () => {
              setIsChangingInterval(true);
              setBillingIntervalError(null);
              try {
                await updateSubscription({
                  action: 'change_interval',
                  target_product_key: targetProductKey,
                });

                // Track AFTER await resolves — prevents reporting a change
                // that never actually happened (e.g. 4xx/5xx from worker).
                trackEvent('subscription_interval_changed', {
                  tier: currentTier,
                  subscription_id: subscription.id,
                  from_interval: subscription.billingInterval ?? undefined,
                  new_interval: newInterval,
                  target_product_key: targetProductKey,
                });

                alert(
                  t('plan_change.interval_success_title', { defaultValue: 'Billing cycle updated' }),
                  t('plan_change.interval_success_message', {
                    interval: newIntervalLabel,
                    defaultValue: `Your billing cycle has been changed to ${newIntervalLabel}. The difference has been prorated.`,
                  }),
                  [{ text: tCommon('confirm') }]
                );
              } catch (error) {
                console.error('[Subscription] Interval change error:', error);
                // Revert selection on failure
                setSelectedInterval(subscription.billingInterval!);
                // Preserve original error semantics: generic "Request failed: N"
                // thrown by the hook for non-OK responses with no server error field
                // maps back to the original localized fallback.
                const rawMessage = error instanceof Error ? error.message : '';
                // Preserve the status code in the fallback message — matches
                // the parallel `useBillingIntervalChange.ts` path which emits
                // "Failed to change billing cycle (<status>)" so support can
                // tell a 4xx user error from a 5xx outage at a glance.
                const statusMatch = rawMessage.match(/Request failed:\s*(\d+)/);
                const status = statusMatch?.[1] ?? '';
                setBillingIntervalError(
                  rawMessage && !rawMessage.startsWith('Request failed:')
                    ? rawMessage
                    : t('errors.change_billing_cycle_with_status', {
                        defaultValue: 'Failed to change billing cycle ({{status}})',
                        status,
                      })
                );
              } finally {
                setIsChangingInterval(false);
              }
            },
          },
        ]
      );
    }
  };

  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState<{
    id: string;
    name: string;
    type?: 'percentage' | 'fixed';
    amount?: number;
    code?: string;
    duration?: 'once' | 'forever' | 'repeating';
    duration_in_months?: number;
  } | null>(null);
  const [promoError, setPromoError] = useState('');
  const [isPromoLoading, setIsPromoLoading] = useState(false);
  const [isPromoExpanded, setIsPromoExpanded] = useState(false);

  // CMS-powered pricing with automatic fallback to constants.ts
  // Type guards ensure runtime safety when mapping local types to CMS types
  const validatedTier = isValidTier(selectedTier) ? selectedTier : 'drama_plus';
  const validatedPlanType = isValidPlanType(planType) ? planType : 'individual';

  const { getPriceForInterval, pricingByInterval, isCMSEnabled: cmsEnabled, error: cmsError } = useSubscriptionPricing(
    validatedTier,
    validatedPlanType
  );

  // Track CMS errors for monitoring (non-blocking, fallback handles it)
  useEffect(() => {
    if (cmsError) {
      trackEvent('cms_pricing_error', {
        tier_id: validatedTier,
        plan_type: validatedPlanType,
        error_message: cmsError.message,
        fallback_used: true,
      });
    }
  }, [cmsError, validatedTier, validatedPlanType]);

  // Track quota upsell shown (Phase 1 Quick Wins)
  useEffect(() => {
    if (source === 'quota_exceeded') {
      trackEvent('quota_upsell_shown', {
        source: 'quota_exceeded',
        tier: selectedTier,
        trigger: 'subscription_screen',
      });
    }
  }, [source, selectedTier]);

// Track screen view (typed pricing funnel event)
  useEffect(() => {
    trackEvent('Pricing_PageViewed', {
      user_id: user?.id,
      user_tier: user ? entitlementTier : 'anonymous',
      source: source === 'quota_exceeded' ? 'quota_limit' : 'direct_link',
    });
  }, [source, user, entitlementTier]);
  // --- Plan Change Handler (for existing subscribers) ---
  const handlePlanChange = async (action: 'upgrade' | 'downgrade', targetProductKey: string) => {
    if (!user || !accessToken) {
      alert(
        t('errors.auth_required_title'),
        t('errors.auth_required_message'),
        [{ text: tCommon('confirm') }]
      );
      return;
    }

    // Determine target tier info for confirmation dialog
    const isUpgrade = action === 'upgrade';
    const targetTierName = isUpgrade ? TIER_NAMES.dramax : TIER_NAMES.drama_plus;
    const renewalDate = subscription?.currentPeriodEnd
      ? new Date(subscription.currentPeriodEnd).toLocaleDateString(undefined, {
          month: 'short', day: 'numeric', year: 'numeric',
        })
      : '---';

    // Show confirmation dialog
    const confirmTitle = isUpgrade
      ? t('plan_change.upgrade_title', { tier: targetTierName, defaultValue: `Upgrade to ${targetTierName}?` })
      : t('plan_change.downgrade_title', { tier: targetTierName, defaultValue: `Switch to ${targetTierName}?` });

    // Estimate proration for upgrades using monthly prices as baseline
    const currentMonthlyPrice = currentTier === 'drama_plus'
      ? PRICING.drama_plus_monthly.price
      : PRICING.dramax_monthly.price;
    const targetMonthlyPrice = isUpgrade
      ? PRICING.dramax_monthly.price
      : PRICING.drama_plus_monthly.price;
    const estimatedProration = isUpgrade
      ? `\n${t('plan_change.estimated_charge', {
          amount: `~$${(targetMonthlyPrice - currentMonthlyPrice).toFixed(2)}`,
          defaultValue: `Estimated charge: ~$${(targetMonthlyPrice - currentMonthlyPrice).toFixed(2)} prorated`,
        })}`
      : '';

    const confirmMessage = isUpgrade
      ? t('plan_change.upgrade_message', {
          defaultValue: 'You\'ll be charged a prorated amount for the rest of this billing period.',
        }) + estimatedProration
      : t('plan_change.downgrade_message', {
          date: renewalDate,
          defaultValue: `Your plan will change at the end of your current period on ${renewalDate}.`,
        });

    alert(confirmTitle, confirmMessage, [
      { text: tCommon('cancel'), style: 'cancel' },
      {
        text: isUpgrade
          ? t('plan_change.confirm_upgrade', { defaultValue: 'Upgrade' })
          : t('plan_change.confirm_downgrade', { defaultValue: 'Switch' }),
        onPress: async () => {
          setIsPlanChangeLoading(true);

          trackEvent('subscription_plan_change_attempted', {
            action,
            target_product_key: targetProductKey,
            current_tier: currentTier,
          });

          try {
            await updateSubscription({
              action,
              target_product_key: targetProductKey,
            });

            trackEvent('subscription_plan_change_completed', {
              action,
              target_product_key: targetProductKey,
              previous_tier: currentTier,
            });

            alert(
              t('plan_change.success_title', { defaultValue: 'Plan Updated' }),
              isUpgrade
                ? t('plan_change.upgrade_success', {
                    tier: targetTierName,
                    defaultValue: `You've been upgraded to ${targetTierName}!`,
                  })
                : t('plan_change.downgrade_success', {
                    tier: targetTierName,
                    date: renewalDate,
                    defaultValue: `Your plan will switch to ${targetTierName} at the end of your current period.`,
                  }),
              [{ text: tCommon('confirm') }]
            );
          } catch (error) {
            console.error('[Subscription] Plan change error:', error);

            // Preserve original error-message semantics: generic "Request failed: N"
            // thrown by the hook for non-OK responses with no server error field maps
            // back to the original localized fallback ("Failed to update subscription").
            const rawMessage = error instanceof Error ? error.message : '';
            const displayMessage = rawMessage && !rawMessage.startsWith('Request failed:')
              ? rawMessage
              : t('errors.update_subscription', { defaultValue: 'Failed to update subscription' });

            trackEvent('subscription_plan_change_failed', {
              action,
              target_product_key: targetProductKey,
              error: rawMessage || 'Unknown error',
            });

            alert(
              t('plan_change.error_title', { defaultValue: 'Plan Change Failed' }),
              t('plan_change.error_message', {
                message: displayMessage || t('errors.try_again'),
                defaultValue: 'Something went wrong. Please try again.',
              }),
              [{ text: tCommon('confirm') }]
            );
          } finally {
            setIsPlanChangeLoading(false);
          }
        },
      },
    ]);
  };

  // --- Promo Code Validation ---
  const validatePromo = async () => {
    if (!promoCode.trim()) return;

    setIsPromoLoading(true);
    setPromoError('');

    try {
      // NocoDB #4128 (2026-05-08): migrated from raw fetch() to authenticatedFetch().
      // Bearer token + Accept-Version + cold-load pre-gate handled by the wrapper.
      const response = await authenticatedFetch(getEdgeFunctionUrl('polar-checkout'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'validate_discount', code: promoCode.trim() }),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result?.data?.discount) {
        setPromoError(t('promo.invalid_code', { defaultValue: 'Invalid promo code' }));
        trackEvent('subscription_promo_failed', {
          code: promoCode.trim(),
          error: result?.error || 'Invalid code',
        });
      } else {
        const discount = result.data.discount;
        setPromoDiscount(discount);
        trackEvent('subscription_promo_applied', {
          code: promoCode.trim(),
          discount_id: discount.id,
          discount_name: discount.name,
          discount_type: discount.type ?? 'unknown',
          discount_value: discount.amount,
        });
      }
    } catch {
      setPromoError(t('promo.error', { defaultValue: 'Failed to validate code' }));
      trackEvent('subscription_promo_failed', {
        code: promoCode.trim(),
        error: 'Network error',
      });
    } finally {
      setIsPromoLoading(false);
    }
  };

  const clearPromo = () => {
    setPromoDiscount(null);
    setPromoCode('');
    setPromoError('');
    setIsPromoExpanded(false);
  };

  const handleSubscribe = async () => {
    // Defensive check: prevent checkout when disabled
    if (!CHECKOUT_ENABLED) {
      return;
    }

    if (!user || user.isAnonymous || !isRealUser) {
      alert(
        t('errors.auth_required_title'),
        t('errors.auth_required_message'),
        [{ text: tCommon('confirm') }]
      );
      return;
    }

    if (isCheckoutAuthPending || authLoading) {
      alert(
        t('errors.checkout_error_title'),
        'Please wait a moment while we finish signing you in.',
        [{ text: tCommon('confirm') }]
      );
      return;
    }

    // Build product key using helper function
    const tierKey = selectedTier;
    const familySuffix = planType === 'family' ? '_family' : '';
    const intervalSuffix = getBillingIntervalSuffix(selectedInterval);
    const productKey = `${tierKey}${familySuffix}${intervalSuffix}` as ProductKey;

    // Get pricing from CMS (with fallback to constants.ts)
    const currentPricing = getPriceForInterval(selectedInterval);

    // Show confirmation dialog BEFORE creating the Polar checkout session.
    // This prevents unnecessary API calls and avoids creating orphaned sessions
    // when the user cancels at the confirmation step.
    const tierName = TIER_NAMES[selectedTier] ?? selectedTier;
    const priceDisplay = displayPricing?.price ? `$${displayPricing.price.toFixed(2)}` : '---';
    const promoText = promoDiscount
      ? `Discount: ${promoDiscount.name ?? promoDiscount.code ?? promoCode}`
      : '';
    const confirmMessage = `Plan: ${tierName} (${selectedInterval})\nPrice: ${priceDisplay}/${selectedInterval}${promoText ? `\n${promoText}` : ''}\nBilling starts today.`;

    const userConfirmed = await new Promise<boolean>((resolve) => {
      alert(
        t('checkout.confirm_title', { defaultValue: 'Confirm Subscription' }),
        confirmMessage,
        [
          {
            text: tCommon('cancel'),
            style: 'cancel',
            onPress: () => resolve(false),
          },
          {
            text: t('checkout.confirm_pay', { defaultValue: 'Confirm & Pay' }),
            onPress: () => resolve(true),
          },
        ],
      );
    });

    if (!userConfirmed) {
      return;
    }

    // Track purchase attempt only after user confirms
    trackEvent('subscription_purchase_attempted', {
      tier: selectedTier,
      interval: selectedInterval,
      plan_type: planType,
      price: currentPricing?.price ?? 0,
      cms_source: cmsEnabled ? 'cms' : 'constants',
    });

    setIsLoading(true);

    try {
      // Create Polar checkout session (only after user confirmed).
      // Migrated from /api/polar/create-checkout (Vercel) to Supabase Edge Function
      // as part of Cloudflare Pages migration (task #1107).
      // Use referral code cached in ref (read once on mount, avoids second async call)
      const storedRefCode = referralCodeRef.current;

      // NocoDB #4128 (2026-05-08): migrated from raw fetch() to authenticatedFetch().
      // Bearer token + Accept-Version + cold-load pre-gate handled by the wrapper.
      const checkoutResponse = await authenticatedFetch(getEdgeFunctionUrl('polar-checkout'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.id,
          product_key: productKey,
          ...(promoDiscount?.id && { discount_id: promoDiscount.id }),
          ...(storedRefCode && isValidReferralCode(storedRefCode) && { referral_code: storedRefCode }),
        }),
      });

      await assertResponseOk(checkoutResponse, t('errors.create_checkout', { defaultValue: 'Failed to create checkout session' }));

      const { data } = await checkoutResponse.json();

      // Validate checkout URL before opening
      // Accept both production (polar.sh) and sandbox (sandbox.polar.sh) URLs
      if (!data?.checkout_url || !/^https:\/\/(sandbox\.)?polar\.sh\/checkout\//.test(data.checkout_url)) {
        console.error('[Subscription] Invalid checkout URL:', data?.checkout_url);
        throw new Error('Invalid checkout URL');
      }

      trackEvent('subscription_checkout_opened', {
        tier: selectedTier,
        interval: selectedInterval,
        plan_type: planType,
        checkout_id: data.checkout_id,
      });

      const canOpen = await Linking.canOpenURL(data.checkout_url);
      if (canOpen) {
        await Linking.openURL(data.checkout_url);
      } else {
        throw new Error(t('errors.cannot_open_url'));
      }
    } catch (error) {
      const authFailureReason = getAuthFailureReason(error);
      const authFailureCategory = getAuthFailureCategory(error);
      console.error('[Subscription] Checkout error:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        authFailureReason,
        authFailureCategory,
      });

      trackEvent('subscription_checkout_failed', {
        tier: selectedTier,
        interval: selectedInterval,
        plan_type: planType,
        error: error instanceof Error ? error.message : 'Unknown error',
        auth_failure_reason: authFailureReason ?? undefined,
        auth_failure_category: authFailureCategory ?? undefined,
      });

      alert(
        t('errors.checkout_error_title'),
        t('errors.checkout_error_message', {
          message: error instanceof Error ? error.message : t('errors.try_again')
        }),
        [{ text: tCommon('confirm') }]
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Get current pricing using CMS hook (with fallback to constants.ts)
  const displayPricing = getPriceForInterval(selectedInterval);

  return (
    <GlassBackground>
      <ScrollView className="flex-1" contentContainerStyle={{ paddingBottom: bottomPadding }}>
        {/* Header with Close Button */}
        <View className="flex-row justify-between items-center p-4">
          <Pressable onPress={() => router.canGoBack() ? router.back() : router.replace('/(tabs)/settings')} className="p-2">
            <Text className="text-2xl">×</Text>
          </Pressable>
        </View>

        {/* Quota Exceeded Banner (conditionally rendered) */}
        {source === 'quota_exceeded' && (
          <View className="mx-4 mb-6 bg-gradient-to-br from-pink-50 to-purple-50 border-2 border-purple-200 rounded-2xl p-6 shadow-lg">
            <Text className="text-2xl font-bold text-purple-900 mb-3">
              {t('quota.exceeded.title')}
            </Text>
            <Text className="text-base text-purple-800 mb-4 leading-relaxed">
              {t('quota.exceeded.message', { used: 3, limit: 3 })}
            </Text>
            <View className="bg-purple-100 border border-purple-300 rounded-xl p-4">
              <Text className="text-base font-semibold text-purple-900 text-center">
                {t('quota.exceeded.cta', { tier: 'Drama+', quota: 500 })}
              </Text>
            </View>
          </View>
        )}

        {/* === Subscriber Plan Change UI === */}
        {isSubscribed && currentTier && (
          <View className="px-4 mb-6">
            {/* Current Plan Info */}
            <GlassCard>
              <View className="py-1">
                <View className="flex-row items-center justify-between mb-2">
                  <Text className="text-lg font-semibold text-gray-900">
                    {t('plan_change.current_plan', {
                      plan: currentPlanName ?? currentTier,
                      defaultValue: `You're on ${currentPlanName ?? currentTier}`,
                    })}
                  </Text>
                  <View className="px-2 py-0.5 rounded-full bg-green-100">
                    <Text className="text-xs font-semibold text-green-700">
                      {t('plan_change.status_active', { defaultValue: 'Active' })}
                    </Text>
                  </View>
                </View>
                {subscription?.currentPeriodEnd && (
                  <Text className="text-sm text-gray-500">
                    {t('plan_change.renews_on', {
                      date: new Date(subscription.currentPeriodEnd).toLocaleDateString(undefined, {
                        month: 'short', day: 'numeric', year: 'numeric',
                      }),
                      defaultValue: `Renews on ${new Date(subscription.currentPeriodEnd).toLocaleDateString(undefined, {
                        month: 'short', day: 'numeric', year: 'numeric',
                      })}`,
                    })}
                  </Text>
                )}
              </View>
            </GlassCard>

            {/* Upgrade: Drama+ -> DramaX (gated until DramaX features complete) */}
            {SHOW_DRAMAX_PLANS && currentTier === 'drama_plus' && (
              <PlanChangeCard
                type="upgrade"
                currentPrice={PRICING.drama_plus_monthly.displayPrice}
                targetPrice={PRICING.dramax_monthly.displayPrice}
                currentLabel={t('plan_change.current_price', { defaultValue: 'Current' })}
                targetLabel={t('plan_change.new_price', { defaultValue: 'DramaX' })}
                title={t('plan_change.upgrade_to_dramax', { defaultValue: 'Upgrade to DramaX' })}
                description={t('plan_change.upgrade_description', {
                  defaultValue: 'Unlock 5,000 character limits, 200 daily analyses, and full Narrative Clarity methodology.',
                })}
                ctaLabel={t('plan_change.upgrade_cta', { defaultValue: 'Upgrade Now' })}
                isLoading={isPlanChangeLoading}
                onPress={() => handlePlanChange('upgrade', 'dramax_monthly')}
              />
            )}

            {/* Downgrade: DramaX -> Drama+ */}
            {currentTier === 'dramax' && (
              <PlanChangeCard
                type="downgrade"
                currentPrice={PRICING.dramax_monthly.displayPrice}
                targetPrice={PRICING.drama_plus_monthly.displayPrice}
                currentLabel={t('plan_change.current_price', { defaultValue: 'Current' })}
                targetLabel={t('plan_change.new_price', { defaultValue: 'Drama+' })}
                title={t('plan_change.switch_to_drama_plus', { defaultValue: 'Switch to Drama+' })}
                description={t('plan_change.downgrade_description', {
                  defaultValue: 'Keep essential features with 2,000 character limits and 100 daily analyses.',
                })}
                ctaLabel={t('plan_change.downgrade_cta', { defaultValue: 'Switch Plan' })}
                isLoading={isPlanChangeLoading}
                onPress={() => handlePlanChange('downgrade', 'drama_plus_monthly')}
              />
            )}

            {/* Billing Interval Selector for existing subscribers */}
            <View className="mt-4">
              <Text className="text-sm font-medium text-gray-700 px-4 mb-2">
                {t('plan_change.change_billing_cycle', { defaultValue: 'Change billing cycle' })}
              </Text>
              {billingIntervalError && (
                <View className="mx-4 mb-2 bg-red-50 border border-red-200 rounded-lg p-2">
                  <Text className="text-xs text-red-700">{billingIntervalError}</Text>
                </View>
              )}
              <BillingIntervalSelector
                selectedInterval={selectedInterval}
                onIntervalChange={handleIntervalChange}
                pricingByInterval={pricingByInterval}
              />
              {/* Price display for selected interval */}
              {displayPricing && (
                <View className="px-4 mt-2">
                  <GlassCard>
                    <View className="items-center py-2">
                      <Text className="text-2xl font-bold text-gray-900">
                        {displayPricing.displayPrice}
                      </Text>
                      {displayPricing.billedAs && (
                        <Text className="text-sm text-gray-500 mt-1">{displayPricing.billedAs}</Text>
                      )}
                      {selectedInterval === subscription?.billingInterval && (
                        <Text className="text-xs text-green-600 font-medium mt-1">
                          {t('plan_change.current_interval', { defaultValue: 'Current billing cycle' })}
                        </Text>
                      )}
                      {(isPlanChangeLoading || isChangingInterval) && (
                        <ActivityIndicator size="small" color="#7C3AED" className="mt-2" />
                      )}
                    </View>
                  </GlassCard>
                </View>
              )}
            </View>
          </View>
        )}

        {/* === Non-Subscriber UI (original checkout flow) === */}
        {!isSubscribed && (
          <>
            {/* Referral incentive banner (NocoDB #1556) */}
            {hasReferral && (
              <View className="mx-4 mb-3 p-4 bg-purple-50 rounded-2xl border border-purple-200">
                <Text className="text-base font-semibold text-purple-900">
                  {tCommon('referral_banner.title')}
                </Text>
                <Text className="text-sm text-purple-700 mt-1">
                  {tCommon('referral_banner.subtitle')}
                </Text>
              </View>
            )}

            {/* Tier Tabs */}
            <TierSwitcher
              selectedTier={selectedTier}
              onTierChange={setSelectedTier}
              showDramaxPlans={SHOW_DRAMAX_PLANS}
            />

            {/* Plan Type Toggle (Individual vs Family) — hidden until family plans are validated */}
            {SHOW_FAMILY_PLANS && (
              <PlanTypeToggle
                planType={planType}
                onPlanTypeChange={setPlanType}
              />
            )}

            {/* Features Section (includes hero banner) */}
            <FeaturesSection
              selectedTier={selectedTier}
              planType={planType}
            />

            {/* Billing Interval Horizontal Scroll */}
            <BillingIntervalSelector
              selectedInterval={selectedInterval}
              onIntervalChange={setSelectedInterval}
              pricingByInterval={pricingByInterval}
            />

            {/* Family Plan Status (if member) — hidden until family plans are validated */}
            {SHOW_FAMILY_PLANS && (
              <FamilyPlanBanner
                familyPlan={familyPlan}
                isFamilyMember={isFamilyMember}
                isFamilyAdmin={isFamilyAdmin}
                onManageFamily={() => router.push('/(tabs)/family-manage')}
              />
            )}

            {/* CTA Button */}
            <PricingCard
              selectedInterval={selectedInterval}
              price={displayPricing?.price ?? 0}
              isLoading={isLoading}
              isAuthPending={isCheckoutAuthPending || authLoading}
              onSubscribe={handleSubscribe}
              checkoutEnabled={CHECKOUT_ENABLED}
            />

            {/* Promo Code Section */}
            <View className="px-4 pb-4">
              {promoDiscount ? (
                /* Applied promo state */
                <GlassCard>
                  <View className="flex-row items-center justify-between">
                    <View className="flex-row items-center flex-1">
                      <Text className="text-green-600 text-lg mr-2">{'✓'}</Text>
                      <View className="flex-1">
                        <Text className="text-sm font-semibold text-green-700">
                          {t('promo.applied', {
                            code: promoDiscount.code ?? promoCode,
                            defaultValue: `${promoDiscount.code ?? promoCode} applied`,
                          })}
                          {promoDiscount.type === 'percentage' && promoDiscount.amount
                            ? ` — ${promoDiscount.amount}% ${t('promo.off', { defaultValue: 'off' })}!`
                            : promoDiscount.type === 'fixed' && promoDiscount.amount
                              ? ` — $${(promoDiscount.amount / 100).toFixed(2)} ${t('promo.off', { defaultValue: 'off' })}!`
                              : ''}
                        </Text>
                        {promoDiscount.duration === 'once' && (
                          <Text className="text-xs text-gray-500">
                            {t('promo.duration_once', { defaultValue: 'First payment only' })}
                          </Text>
                        )}
                        {promoDiscount.duration === 'forever' && (
                          <Text className="text-xs text-green-600">
                            {t('promo.duration_forever', { defaultValue: 'Applied forever' })}
                          </Text>
                        )}
                        {promoDiscount.duration === 'repeating' && (
                          <Text className="text-xs text-gray-500">
                            {t('promo.duration_repeating', {
                              months: promoDiscount.duration_in_months,
                              defaultValue: `First ${promoDiscount.duration_in_months} months`,
                            })}
                          </Text>
                        )}
                      </View>
                    </View>
                    <Pressable
                      onPress={clearPromo}
                      className="ml-2 px-3 py-1"
                      testID="promo-remove-button"
                    >
                      <Text className="text-sm text-gray-500">
                        {t('promo.remove', { defaultValue: 'Remove' })}
                      </Text>
                    </Pressable>
                  </View>
                </GlassCard>
              ) : (
                /* Collapsible promo input */
                <Pressable
                  onPress={() => setIsPromoExpanded(!isPromoExpanded)}
                  testID="promo-toggle-button"
                >
                  <Text className="text-sm text-purple-600 text-center">
                    {isPromoExpanded
                      ? t('promo.hide', { defaultValue: 'Hide promo code' })
                      : t('promo.have_code', { defaultValue: 'Have a promo code?' })}
                  </Text>
                </Pressable>
              )}

              {isPromoExpanded && !promoDiscount && (
                <View className="mt-3">
                  <GlassCard>
                    <View className="flex-row items-center">
                      <TextInput
                        value={promoCode}
                        onChangeText={(text) => {
                          setPromoCode(text);
                          if (promoError) setPromoError('');
                        }}
                        placeholder={t('promo.placeholder', { defaultValue: 'Enter promo code' })}
                        placeholderTextColor="#9CA3AF"
                        autoCapitalize="characters"
                        autoCorrect={false}
                        className="flex-1 text-base text-gray-900 py-2"
                        testID="promo-code-input"
                        editable={!isPromoLoading}
                      />
                      <Pressable
                        onPress={validatePromo}
                        disabled={isPromoLoading || !promoCode.trim()}
                        className={`ml-3 px-4 py-2 rounded-full ${
                          isPromoLoading || !promoCode.trim()
                            ? 'bg-gray-200'
                            : 'bg-purple-600 active:bg-purple-700'
                        }`}
                        testID="promo-apply-button"
                      >
                        {isPromoLoading ? (
                          <ActivityIndicator color="white" size="small" />
                        ) : (
                          <Text
                            className={`text-sm font-semibold ${
                              !promoCode.trim() ? 'text-gray-400' : 'text-white'
                            }`}
                          >
                            {t('promo.apply', { defaultValue: 'Apply' })}
                          </Text>
                        )}
                      </Pressable>
                    </View>
                    {promoError ? (
                      <Text className="text-sm text-red-500 mt-2">
                        {promoError}
                      </Text>
                    ) : null}
                  </GlassCard>
                </View>
              )}
            </View>
          </>
        )}
      </ScrollView>
    </GlassBackground>
  );
}
